import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import BudgetTileRow from "@/components/ui/v1/BudgetTileRow";
import { Button } from "@/components/ui/v2/button";
import { Switch } from "@/components/ui/v2/switch";
import { DataTable } from "@/components/ui/v2/table/data-table";
import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { invoiceQueryKeys } from "@/features/invoices/api/queryKeys";
import { CreditBudgetDialog } from "@/features/transactions/credit";
import { DebitBudgetDialog } from "@/features/transactions/debit";
import { useTransactionTableColumns } from "@/features/transactions/tableColumns/useTransactionTableColumns";
import { normalizeEventType } from "@/features/transactions/utils/eventTypeUtils";
import { ClientBudgetProvider } from "@/lib/contexts/ClientBudget/ClientBudgetProvider";
import { TransactionProvider } from "@/lib/contexts/Transaction/TransactionProvider";
import { useClientBudget } from "@/lib/hooks/useClientBudget";
import { useClients } from "@/lib/hooks/useClients";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSortSearchParams } from "@/lib/hooks/useSortSearchParams";
import useToast from "@/lib/hooks/useToast";
import { useTransaction } from "@/lib/hooks/useTransaction";
import {
  BudgetEventType,
  transactionDirectionMap,
  transactionTypeMap,
} from "@/lib/utils/clientBudgetEventTypeMap";
import { queryClient } from "@/main";
import { PaymentTypeEnum } from "@/types/client/ClientType";
import { GetClientResponse } from "@/types/response/GetClientResponse";
import { PagedResponse } from "@/types/response/PagedResponse";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";
import { TransactionType } from "@/types/transaction/TransactionType";

interface TransactionsContentProps {
  clientId: string;
  descending?: boolean;
  orderBy?: string;
  page: number;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  search?: string;
}

const TransactionsContent = ({
  clientId,
  descending,
  orderBy,
  page,
  pageSize,
  setPageSize,
}: TransactionsContentProps) => {
  const { client } = useClients();
  const { setPage } = usePageSearchParams();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const { transactions, transactionsFetching, transactionsLoading } =
    useTransaction();
  const { transactionTableColumns } = useTransactionTableColumns();
  const { updateMonthlyBudget, updateAllowOverspend } = useClientBudget();
  const [monthlyBudget, setMonthlyBudget] = useState<number | undefined>(
    client?.monthlyBudget
  );
  const [isDebitDialogOpen, setIsDebitDialogOpen] = useState(false);
  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);

  const handleDeditBudget = () => {
    setIsDebitDialogOpen(true);
  };

  const handleCreditBudget = () => {
    setIsCreditDialogOpen(true);
  };

  const handleDebitDialogSuccess = () => {
    setIsDebitDialogOpen(false);
  };

  const handleDebitDialogCancel = () => {
    setIsDebitDialogOpen(false);
  };

  const handleCreditDialogSuccess = () => {
    setIsCreditDialogOpen(false);
  };

  const handleCreditDialogCancel = () => {
    setIsCreditDialogOpen(false);
  };

  useEffect(() => {
    if (client?.monthlyBudget !== undefined) {
      setMonthlyBudget(client.monthlyBudget);
    }
  }, [client?.monthlyBudget]);

  const handleSuccess = () => {
    // Success handler for BudgetTileRow
  };

  const handleAllowOverspendChange = (checked: boolean) => {
    if (updateAllowOverspend.isPending) return;

    updateAllowOverspend.mutate(
      { allowOverspend: checked },
      {
        onSuccess: () => {
          showToast(
            t("admin.client.clientBudgetAction.success", { action: "updated" }),
            "success"
          );

          queryClient.setQueryData(
            clientQueryKeys.detail(clientId ?? ""),
            (oldData: GetClientResponse) => {
              if (!oldData) return oldData;
              return { ...oldData, overspendAllowed: checked };
            }
          );

          queryClient.setQueryData(
            [
              ...invoiceQueryKeys.cache.transactions.byClient(clientId),
              descending,
              orderBy,
              page,
              pageSize,
            ],
            (oldData: PagedResponse<TransactionType>) => {
              if (!oldData) return oldData;
              const newTransaction: TransactionType = {
                eventType: BudgetEventType.OVERSPEND_ALLOWED_CHANGED,
                timestamp: new Date().toISOString(),
                clientId2: client?.id2 ?? uuidv4(),
                eventData: {
                  allocableBudget: client?.allocableBudget,
                  amount: 0,
                  newAllowOverspend: checked,
                },
                eventId: uuidv4(),
                metadata: null,
              };
              const newData = [newTransaction, ...(oldData.data || [])].slice(
                0,
                oldData.pageSize
              );
              return {
                ...oldData,
                data: newData,
                totalRecords: oldData.totalRecords + 1,
              };
            }
          );
        },
        onError: (error: AxiosError<ValidationErrorResponse>) => {
          showToast(
            t(
              error.response?.data?.[0]?.errorCode
                ? `errorCodes.${error.response?.data?.[0]?.errorCode}`
                : "common.error",
              { action: "updated" }
            ),
            "error"
          );
        },
      }
    );
  };

  const data = transactions?.data
    ? transactions.data.map(({ eventType, ...rest }: TransactionType) => {
      const normalizedType = normalizeEventType(eventType);
      return {
        ...rest,
        eventData: {
          ...rest.eventData,
          amount: rest.eventData.amount ?? 0,
        },
        eventType,
        type: transactionTypeMap[normalizedType] || "neutral",
        direction: transactionDirectionMap[normalizedType],
      };
    })
    : [];

  return (
    <ModernPageLayout title={t("common.transactions")}>
      {client?.paymentType === PaymentTypeEnum.SUBSCRIPTION && (
        <>
          <BudgetTileRow
            allocatedBudget={client?.allocatedBudget}
            availableBudget={client?.allocableBudgetWithoutOverspend}
            clientId={clientId}
            currentBudget={client?.currentBudget}
            isLoading={updateMonthlyBudget.isPending}
            monthlyBudget={monthlyBudget}
            onEditSuccess={handleSuccess}
          />
          <div
            style={{
              justifyContent: "flex-end",
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: "10px",
              }}
            >
              <span className="text-base font-semibold">
                {t("actions.allow", { entity: t("common.overspend") })}
              </span>
              <Switch
                disabled={
                  client &&
                  client.overspendAllowed &&
                  client.allocableBudgetWithoutOverspend < 0
                }
                checked={client?.overspendAllowed}
                onCheckedChange={handleAllowOverspendChange}
              />
            </div>
          </div>
        </>
      )}
      {client?.paymentType === PaymentTypeEnum.SUBSCRIPTION && (
        <div className="flex justify-end gap-2">
          <Button onClick={handleCreditBudget}>
            {`${t("common.credit")} ${t(`common.budget`)}`}
          </Button>
          <Button variant="outline" onClick={handleDeditBudget}>
            {`${t("common.debit")} ${t(`common.budget`)}`}
          </Button>
        </div>
      )}
      <DataTable<TransactionType>
        columns={transactionTableColumns}
        data={data}
        isLoading={transactionsFetching || transactionsLoading}
        page={page}
        pageSize={pageSize}
        totalRecords={transactions?.totalRecords ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowId={(row) => row.eventId}
      />
      <DebitBudgetDialog
        open={isDebitDialogOpen}
        onOpenChange={setIsDebitDialogOpen}
        onSuccess={handleDebitDialogSuccess}
        onCancel={handleDebitDialogCancel}
        client={client}
        clientId={clientId}
      />
      <CreditBudgetDialog
        open={isCreditDialogOpen}
        onOpenChange={setIsCreditDialogOpen}
        onSuccess={handleCreditDialogSuccess}
        onCancel={handleCreditDialogCancel}
        client={client}
        clientId={clientId}
      />
    </ModernPageLayout>
  );
};

const TransactionsPage = () => {
  const { clientId } = useParams();
  const { orderBy, descending } = useSortSearchParams();
  const { page } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();

  if (!clientId) return null;

  return (
    <ClientBudgetProvider clientId={clientId}>
      <TransactionProvider
        clientId={clientId}
        descending={descending}
        orderBy={orderBy}
        page={page}
        pageSize={pageSize}
      >
        <TransactionsContent
          clientId={clientId}
          descending={descending}
          pageSize={pageSize}
          setPageSize={setPageSize}
          orderBy={orderBy}
          page={page}
        />
      </TransactionProvider>
    </ClientBudgetProvider>
  );
};

export default TransactionsPage;
